import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Switch,
    Tooltip,
    Stack,
    PDCAlpha as alpha,
} from "@mzc-pdc/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Typography from "@components_v2/typography/typography";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import { useDispatch, useSelector } from "react-redux";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import useUserDetail from "@hooks/useUserDetail";
import UserDetailPopper, { UserDetailButton } from "src/features/user-detail-popper/user-detail-popper";
import EditSubmitButtonGroup from "@components_v2/edit-submit-button-group/edit-submit-button-group";
import {
    IconEditSmall,
    ImageCategoryGroupOn,
    ImageDefaultCustomContentDark,
    ImageFolderSmallOn,
} from "@mzc-cloudplex/icons";
import validateInput from "@cores/validateInput";
import { getCategoryGroups } from "@modules/categoryGroupsManagement";
import { FOLDER_VIEW_DOMAINS, LIMIT_CHAR, REFERENCE_FIELDS_TYPE } from "@constants";
import useCustomContent from "@hooks/useCustomContent";
import StatusBadge from "@components_v2/status-badge/status-badge";
import EditButton from "@components_v2/edit-button/edit-button";
import SelectFolderPathModal from "@components/modals/SelectFolderPath";
import { useHistory } from "react-router-dom";
import menuService from "@services/menuService";
import { UsersInput, UsersInputV2 } from "@features/users-input";
import { capitalize } from "@utils/convert-string";
import compareVersions, { VERSION_CM_11330_USERS_TEAMS_ACCESS_V3 } from "@cores/version";
import { useParams } from "react-router";
import DialogAddCustomContentResourcesV2 from "@routes/custom-contents/dialog-add-custom-content-resources-v2";
import { queryParamsParse } from "@components/params";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import TextField from "@components_v2/text-field/text-field";

const PanelOverview = ({ domain, schemaId, data }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { contentId } = useParams();

    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();
    const { getResourceTableColumns } = useCustomContent({
        pageType: "detail",
    });
    const { getVirtualFolderFullPath } = useVirtualFolderPath({
        domain,
        subDomain: schemaId,
    });

    const project = useSelector(({ project }) => project);
    const stage = useSelector(({ stage }) => stage);
    const categoryGroups = useSelector(({ categoryGroupsManagement }) => categoryGroupsManagement.categoryGroups);

    const [editValue, setEditValue] = useState({});
    const [editValueActive, setEditValueActive] = useState({});
    const [actionPending, setActionPending] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [modal, setModal] = useState(null);
    const [folderLocation, setFolderLocation] = useState("");

    const virtualFolderRef = useRef(null);

    const projectId = useMemo(() => {
        const { projectId: dataProjectId } = queryParamsParse(location.search);

        if (dataProjectId) return dataProjectId;
        return project.id;
    }, [project.id]);

    const onToggleModal = (key = null, value = false) => {
        if (value) {
            setModal(key);
        } else {
            setModal("");
        }
    };

    // 1.0.80 Note: status하고 일반 값의 api가 달라서 props로 두개를 따로 받아야하는 상황이라 타입에 따른 분기처리를 줄이려고 data안에 onSubmitEditValue를 넣게 했습니다.
    // 일반 값 수정 부분이 많아지면 onSubmitEditValue가 같은게 반복적으로 넣어줘야하는 단점이 있긴 할 것 같습니다.
    const onHandleClickSubmitEditValue = async (id, onSubmitEditValue) => {
        try {
            setActionPending({ ...actionPending, [id]: true });
            await onSubmitEditValue({ type: id, editValue: editValue[id] });
            setEditValueActive({ ...actionPending, [id]: undefined });
        } catch (error) {
            console.log(error);
        } finally {
            setActionPending({ ...actionPending, [id]: undefined });
        }
    };

    const onHandleSubmitPreviewAsset = async (assetIds, onSubmitEditValue) => {
        try {
            setActionPending({ ...actionPending, previewAsset: true });
            await onSubmitEditValue({ type: "previewAsset", editValue: { id: assetIds[0] } });
        } catch (error) {
            console.log(error);
        } finally {
            setActionPending({ ...actionPending, previewAsset: undefined });
        }
    };

    const onHandleLoadCategoryGroups = () => {
        dispatch(
            getCategoryGroups(project.id, {
                name: "",
                creator: "",
                status: "ACTIVE",
            }),
        );
    };

    const getFolderName = (value) => {
        if (value?.virtualFolder?.name) return value?.virtualFolder?.name;

        if (!folderLocation) return "";
        return folderLocation?.slice(folderLocation?.lastIndexOf("/"));
    };

    const onHandleClickFolderButton = (value) => {
        if (!stage || !project || !value || !domain || project.id !== projectId) return; //Note: 다른 프로젝트에서는 폴더를 눌러도 이동하지 않도록 방지

        let endpoint = "";
        switch (domain) {
            case FOLDER_VIEW_DOMAINS.ASSETS:
                endpoint = menuService.paths.assetsAssets(stage.id, project.id);
                break;
            case FOLDER_VIEW_DOMAINS.ARCHIVED_ASSETS:
                endpoint = menuService.paths.archiveAssets(stage.id, project.id);
                break;
            case FOLDER_VIEW_DOMAINS.CUSTOM_CONTENTS:
                endpoint = menuService.paths.contentsCustomContents(stage.id, project.id, schemaId);
                break;
            case FOLDER_VIEW_DOMAINS.COLLECTIONS:
                endpoint = menuService.paths.assetsCollections(stage.id, project.id);
                break;
        }

        history.push(
            `${endpoint}?activeView=hierarchy&hierarchyType=folder&virtualDriveId=${value.virtualDrive.id}&virtualFolderId=${value.virtualFolder.id}`,
        );
    };

    const getComponentByType = ({
        type,
        id,
        subtitle,
        value,
        icon,
        isEditable,
        isEditButtonDisabled,
        validation,
        onSubmitEditValue,
        ...props
    }) => {
        if (value === undefined) return null;

        if (type === "image") {
            return (
                <>
                    {modal === "changePreview" && (
                        <DialogAddCustomContentResourcesV2
                            contentId={contentId}
                            maxLimit={1}
                            resource={{
                                type: "PREVIEW",
                                detailType: REFERENCE_FIELDS_TYPE.IMAGE_ASSETS,
                            }}
                            onClose={() => onToggleModal()}
                            hideCheckbox={true}
                            onSubmitCallback={(_, assetIds) => onHandleSubmitPreviewAsset(assetIds, onSubmitEditValue)}
                            columns={getResourceTableColumns("PREVIEW_ASSET")}
                        />
                    )}
                    <ListItemText
                        disableTypography
                        primary={
                            <Typography
                                variant={"body2"}
                                color={theme.palette.greySecondary[600]}
                                component={"p"}
                                gutterBottom
                            >
                                {t(`common::label::${subtitle}`, subtitle)}
                            </Typography>
                        }
                        secondary={
                            <Grid
                                container
                                alignItems={"center"}
                                justifyContent={"center"}
                                maxWidth={432}
                                height={242}
                                sx={{ backgroundColor: theme.palette.secondary[50], position: "relative" }}
                            >
                                {value ? (
                                    <img
                                        src={value?.previews?.[0]?.accessUrl ?? value?.accessUrls?.[0]}
                                        loading="lazy"
                                        alt={""}
                                        height={242}
                                    />
                                ) : (
                                    <ImageDefaultCustomContentDark width={46} height={56} htmlColor={"#7f96e3"} />
                                )}
                                <Tooltip title={t(`common::label::${"Edit"}`)}>
                                    <IconButton
                                        className={"btn-edit"}
                                        size={"xsmall"}
                                        circled
                                        variant={"dashed"}
                                        disabled={isEditButtonDisabled}
                                        onClick={() => {
                                            onToggleModal("changePreview", true);
                                        }}
                                        sx={{
                                            position: `absolute`,
                                            right: 8,
                                            top: 8,
                                        }}
                                    >
                                        <IconEditSmall size={12} />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        }
                    />
                </>
            );
        }
        if (type === "text") {
            return (
                <>
                    <ListItemText
                        disableTypography
                        primary={
                            <>
                                <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                                    {t(`common::label::${subtitle}`, subtitle)}
                                </Typography>
                                {isEditable && !editValueActive[id] && (
                                    <EditButton
                                        disabled={isEditButtonDisabled}
                                        onClick={() => {
                                            setEditValueActive({
                                                ...editValueActive,
                                                [id]: true,
                                            });
                                            setEditValue({ ...editValue, [id]: value ?? "" });
                                        }}
                                    />
                                )}
                            </>
                        }
                        secondary={
                            <Grid item sx={{ mt: 1 }}>
                                {!editValueActive[id] ? (
                                    <Typography
                                        variant={"body1_400"}
                                        component={"span"}
                                        sx={{ color: theme.palette.greySecondary.main }}
                                    >
                                        {value || "-"}
                                    </Typography>
                                ) : (
                                    <>
                                        <TextField
                                            fullWidth
                                            type={`text`}
                                            value={editValue[id]}
                                            error={validationErrors[id]?.level}
                                            helperText={validationErrors?.[id]?.message ?? ""}
                                            maxLength={validation?.maxLength}
                                            length={(editValue[id] || "").length}
                                            onChange={(e) => {
                                                if (validation) {
                                                    setValidationErrors({
                                                        ...validationErrors,
                                                        [id]: validateInput(e.target.value, validation),
                                                    });
                                                }
                                                setEditValue({
                                                    ...editValue,
                                                    [id]: e.target.value,
                                                });
                                            }}
                                        />
                                        <EditSubmitButtonGroup
                                            submitDisabled={editValue[id] === value || actionPending[id]}
                                            submitPending={actionPending[id]}
                                            onClickSubmit={() => {
                                                onHandleClickSubmitEditValue(id, onSubmitEditValue);
                                            }}
                                            onClickClose={() => {
                                                setEditValueActive({
                                                    ...editValueActive,
                                                    [id]: undefined,
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            </Grid>
                        }
                    />
                </>
            );
        }
        if (type === "textarea") {
            return (
                <>
                    <ListItemText
                        disableTypography
                        primary={
                            <>
                                <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                                    {t(`common::label::${subtitle}`, subtitle)}
                                </Typography>
                                {isEditable && !editValueActive[id] && (
                                    <EditButton
                                        disabled={isEditButtonDisabled}
                                        onClick={() => {
                                            setEditValueActive({
                                                ...editValueActive,
                                                [id]: true,
                                            });
                                            setEditValue({ ...editValue, [id]: value ?? "" });
                                        }}
                                    />
                                )}
                            </>
                        }
                        secondary={
                            <Grid item sx={{ mt: 1 }}>
                                {!editValueActive[id] ? (
                                    <Typography
                                        variant={"body1_400"}
                                        component={"span"}
                                        sx={{ color: theme.palette.greySecondary.main }}
                                    >
                                        {value || "-"}
                                    </Typography>
                                ) : (
                                    <>
                                        <TextField
                                            fullWidth
                                            multiline
                                            type={`text`}
                                            value={editValue[id]}
                                            error={validationErrors[id]?.level}
                                            helperText={validationErrors?.[id]?.message ?? ""}
                                            maxLength={validation?.maxLength}
                                            length={(editValue[id] || "").length}
                                            onChange={(e) => {
                                                if (validation) {
                                                    setValidationErrors({
                                                        ...validationErrors,
                                                        [id]: validateInput(e.target.value, {
                                                            ...validation,
                                                        }),
                                                    });
                                                }
                                                setEditValue({
                                                    ...editValue,
                                                    [id]: e.target.value,
                                                });
                                            }}
                                        />
                                        <EditSubmitButtonGroup
                                            submitDisabled={editValue[id] === value || actionPending[id]}
                                            submitPending={actionPending[id]}
                                            onClickSubmit={() => {
                                                onHandleClickSubmitEditValue(id, onSubmitEditValue);
                                            }}
                                            onClickClose={() => {
                                                setEditValueActive({
                                                    ...editValueActive,
                                                    [id]: undefined,
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            </Grid>
                        }
                    />
                </>
            );
        } else if (type === "iconText") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid item sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                            {icon}
                            <Typography
                                variant={"body1_400"}
                                component={"span"}
                                sx={{
                                    marginLeft: "8px !important",
                                }}
                            >
                                {t(`common::label::${value}`)}
                            </Typography>
                        </Grid>
                    }
                />
            );
        } else if (type === "status") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <>
                            <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                                {t(`common::label::${subtitle}`, subtitle)}
                            </Typography>
                            {isEditable && !editValueActive[id] && (
                                <EditButton
                                    disabled={isEditButtonDisabled}
                                    onClick={() => {
                                        setEditValueActive({ ...editValueActive, [id]: true });
                                        setEditValue({ ...editValue, [id]: value ?? "" });
                                    }}
                                />
                            )}
                        </>
                    }
                    secondary={
                        <>
                            <Grid
                                item
                                sx={{
                                    mt: 1,
                                    border: editValueActive[id] ? `1px solid ${theme.palette.primary[400]}` : undefined,
                                    borderRadius: "2px",
                                    padding: editValueActive[id] ? "15px 20px" : 0,
                                }}
                            >
                                {!editValueActive[id] ? (
                                    <>
                                        <StatusBadge status={value} showText={true} />
                                    </>
                                ) : (
                                    <>
                                        <StatusBadge status={editValue[id]} showText={true} />
                                        <Switch
                                            color={"success"}
                                            checked={editValue[id] === "ACTIVE"}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                setEditValue({
                                                    ...editValue,
                                                    [id]: e.target.checked ? "ACTIVE" : "INACTIVE",
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            </Grid>
                            {editValueActive[id] && (
                                <EditSubmitButtonGroup
                                    submitDisabled={editValue[id] === value || actionPending[id]}
                                    submitPending={actionPending[id]}
                                    onClickSubmit={() => {
                                        onHandleClickSubmitEditValue(id, onSubmitEditValue);
                                    }}
                                    onClickClose={() => {
                                        setEditValueActive({
                                            ...editValueActive,
                                            [id]: undefined,
                                        });
                                    }}
                                />
                            )}
                        </>
                    }
                />
            );
        } else if (type === "date") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid container alignItems={`center`} mt={1}>
                            <Typography variant={"body1_400"} color={theme.palette.greySecondary.main}>
                                {`${value.at ? convertUtcToLocal(value.at) : "-"} `}
                                {!value.hideOwner && (
                                    <>
                                        {`by `}
                                        <Button
                                            variant={"text"}
                                            color={`inherit`}
                                            size={"small"}
                                            sx={{
                                                minWidth: 0,
                                                textAlign: `left`,
                                            }}
                                            onClick={(e) => handleUserProfileClick(e, value.by?.id)}
                                        >
                                            {value.by?.name ?? value.by?.id}
                                        </Button>
                                    </>
                                )}
                            </Typography>
                        </Grid>
                    }
                />
            );
        } else if (type === "owner") {
            const user = { id: editValue[id]?.id, name: editValue[id]?.name };

            return (
                <ListItemText
                    disableTypography
                    primary={
                        <>
                            <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                                {t(`common::label::${subtitle}`, subtitle)}
                            </Typography>
                            {isEditable && !editValueActive[id] && (
                                <EditButton
                                    disabled={isEditButtonDisabled}
                                    onClick={() => {
                                        setEditValueActive({ ...editValueActive, [id]: true });
                                        setEditValue({ ...editValue, [id]: value ?? "" });
                                    }}
                                />
                            )}
                        </>
                    }
                    secondary={
                        <>
                            <Grid
                                item
                                sx={{
                                    mt: 1,
                                    // border: editValueActive[id]
                                    //     ? `1px solid ${theme.palette.primary[400]}`
                                    //     : undefined,
                                    // borderRadius: "2px",
                                    // padding: editValueActive[id] ? "15px 20px" : 0,
                                }}
                            >
                                {!editValueActive[id] ? (
                                    <>
                                        {value?.name ? (
                                            <UserDetailButton
                                                maxWidth={250}
                                                onClick={(e) => handleUserProfileClick(e, value.id)}
                                                userName={value.name}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {compareVersions(stage.version, VERSION_CM_11330_USERS_TEAMS_ACCESS_V3) >= 0 ? (
                                            <UsersInputV2
                                                user={user}
                                                onDeleteUser={() => {
                                                    setEditValue({ ...editValue, [id]: undefined });
                                                }}
                                                onAddUser={(user) => {
                                                    setEditValue({ ...editValue, [id]: user });
                                                }}
                                                isAvatarIconRequired={true}
                                                disabledSearchUsers={false}
                                                isEdit={true}
                                            />
                                        ) : (
                                            <UsersInput
                                                user={{ id: editValue[id].id, name: editValue[id].name }}
                                                onDeleteUser={() => {
                                                    setEditValue({ ...editValue, [id]: undefined });
                                                }}
                                                onAddUser={(user) => {
                                                    setEditValue({ ...editValue, [id]: user });
                                                }}
                                                isAvatarIconRequired={true}
                                                disabledSearchUsers={false}
                                                isEdit={true}
                                            />
                                        )}
                                    </>
                                )}
                            </Grid>
                            {editValueActive[id] && (
                                <EditSubmitButtonGroup
                                    submitDisabled={editValue[id] === value || actionPending[id]}
                                    submitPending={actionPending[id]}
                                    onClickSubmit={() => {
                                        onHandleClickSubmitEditValue(id, onSubmitEditValue);
                                    }}
                                    onClickClose={() => {
                                        setEditValueActive({
                                            ...editValueActive,
                                            [id]: undefined,
                                        });
                                    }}
                                />
                            )}
                        </>
                    }
                />
            );
        } else if (type === "categoryGroup") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <>
                            <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                                {t(`common::label::${subtitle}`, subtitle)}
                            </Typography>
                            {isEditable && !editValueActive[id] && (
                                <Tooltip title={t(`common::label::${"Edit"}`)}>
                                    <IconButton
                                        disabled={isEditButtonDisabled}
                                        className={"btn-edit"}
                                        size={"xsmall"}
                                        circled
                                        variant={"dashed"}
                                        onClick={() => {
                                            onHandleLoadCategoryGroups();
                                            setEditValueActive({ ...editValueActive, [id]: true });
                                            setEditValue({ ...editValue, [id]: value ?? "" });
                                        }}
                                    >
                                        <IconEditSmall size={12} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    }
                    secondary={
                        <>
                            <Grid container flexDirection={"row"} alignItems={"center"}>
                                <Grid item>
                                    <ImageCategoryGroupOn width={16} height={16} />
                                </Grid>
                                <Grid
                                    item
                                    flex={1}
                                    sx={{
                                        mt: 1,
                                        ml: 1,
                                        borderRadius: "2px",
                                        order: editValueActive[id]
                                            ? `1px solid ${theme.palette.primary[400]}`
                                            : undefined,
                                        // padding: editValueActive[id] ? "15px 20px" : 0,
                                    }}
                                >
                                    {!editValueActive[id] ? (
                                        <Typography
                                            component={`span`}
                                            variant={"body1_400"}
                                            color={theme.palette.greySecondary.main}
                                            sx={{ ml: 1 }}
                                        >
                                            {value?.name}
                                        </Typography>
                                    ) : (
                                        <Grid width={"100%"}>
                                            <Select
                                                fullWidth
                                                id="demo-simple-select"
                                                labelId="demo-simple-select-label"
                                                onChange={(e) => {
                                                    const category = categoryGroups.find(
                                                        (category) => category.id === e.target.value,
                                                    );

                                                    setEditValue({
                                                        ...editValue,
                                                        [id]: { id: category.id, name: category.name },
                                                    });
                                                }}
                                                value={editValue[id]?.id}
                                                sx={{ ".MuiMenuPaper-root": { width: "350px" } }}
                                            >
                                                {categoryGroups?.map((category) => (
                                                    <MenuItem
                                                        key={`panel-overview-category-group-${category.id}`}
                                                        sx={{
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-all",
                                                        }}
                                                        dense
                                                        value={category.id}
                                                    >
                                                        {category.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            {editValueActive[id] && (
                                <EditSubmitButtonGroup
                                    submitDisabled={editValue[id] === value || actionPending[id]}
                                    submitPending={actionPending[id]}
                                    onClickSubmit={() => {
                                        onHandleClickSubmitEditValue(id, onSubmitEditValue);
                                    }}
                                    onClickClose={() => {
                                        setEditValueActive({
                                            ...editValueActive,
                                            [id]: undefined,
                                        });
                                    }}
                                />
                            )}
                        </>
                    }
                />
            );
        } else if (type === "folder") {
            const parsedDomain =
                domain === FOLDER_VIEW_DOMAINS.CUSTOM_CONTENTS ? "Content" : capitalize(domain, true).slice(0, -1);

            return (
                <>
                    <ListItemText
                        disableTypography
                        primary={
                            <>
                                <SelectFolderPathModal
                                    domain={parsedDomain}
                                    submitText={`Select`}
                                    width={"600px"}
                                    title={`Select ${parsedDomain?.toLowerCase()} in a specific folder?`}
                                    subFoldersOption={false}
                                    onSubmit={async (value) => {
                                        await onSubmitEditValue({ type, editValue: value });
                                        onToggleModal();
                                    }}
                                    onClose={() => onToggleModal()}
                                    open={modal === "selectFolderPath"}
                                    driveInfo={value.driveInfo}
                                />
                                <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                                    {t(`common::label::${subtitle}`, subtitle)}
                                </Typography>
                                {isEditable && !editValueActive[id] && (
                                    <EditButton
                                        disabled={isEditButtonDisabled}
                                        onClick={() => {
                                            onToggleModal("selectFolderPath", true);
                                        }}
                                    />
                                )}
                            </>
                        }
                        secondary={
                            <Grid item sx={{ mt: 1 }}>
                                <Grid container flexDirection={"row"} alignItems={"center"}>
                                    <Grid item mr={0.5}>
                                        <ImageFolderSmallOn height={16} width={16} />
                                    </Grid>
                                    <Tooltip title={folderLocation}>
                                        <Button
                                            variant={"text"}
                                            size={"small"}
                                            color={"secondary"}
                                            disabled={props.folderNameButtonDisabled}
                                            onClick={() => onHandleClickFolderButton(value)}
                                        >
                                            <Typography color={theme.palette.greySecondary.main} variant={"body1_400"}>
                                                {getFolderName(value)}
                                            </Typography>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        }
                    />
                </>
            );
        } else if (type === "project") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid container sx={{ mt: 1 }} flexDirection={"row"} alignItems={"center"}>
                            <Typography
                                variant={"body1_400"}
                                component={"span"}
                                sx={{ color: theme.palette.greySecondary.main }}
                            >
                                {value.name}
                                {value.id && (
                                    <span style={{ color: theme.palette.greySecondary[600] }}>{` (${value.id})`}</span>
                                )}
                            </Typography>
                        </Grid>
                    }
                />
            );
        } else if (type === "component") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body2"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid container sx={{ mt: 1 }} flexDirection={"row"} alignItems={"center"}>
                            {value}
                        </Grid>
                    }
                />
            );
        }
    };

    const getFolderLocation = useCallback(async () => {
        if (!virtualFolderRef.current) return;

        const { virtualDrive, virtualFolder } = virtualFolderRef.current;
        const folderFullPath = await getVirtualFolderFullPath({
            driveId: virtualDrive.id,
            folderId: virtualFolder.id,
        });

        setFolderLocation(folderFullPath);
    }, [getVirtualFolderFullPath]);

    useEffect(() => {
        if (data) {
            // attributions 배열에서 folder type 객체의 value를 직접 가져오기
            let foundFolderData = null;
            for (const group of data) {
                const folderAttribution = group?.attributions?.find((attribution) => attribution.type === "folder");
                if (folderAttribution) {
                    foundFolderData = folderAttribution.value;
                    break;
                }
            }

            if (foundFolderData && virtualFolderRef.current?.virtualFolder?.id !== foundFolderData?.virtualFolder?.id) {
                virtualFolderRef.current = foundFolderData;
                getFolderLocation();
            }
        }
    }, [data, getFolderLocation]);

    return (
        <Box pt={2.5} pl={2.5} pr={1} pb={2.5}>
            {data?.map((group, index) => (
                <div key={index}>
                    <Stack>
                        {group.attributions?.length > 0 && (
                            <Grid container>
                                <List
                                    disablePadding
                                    sx={{ p: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}
                                >
                                    {group.attributions
                                        ?.filter((attribution) => !attribution.hidden)
                                        ?.map((attribution, index) => (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    pt: index > 0 ? 1 : 0,
                                                    px: 1.5,
                                                    py: 0.25,
                                                    borderRadius: 0.5,
                                                    transition: "background-color 0.3s",

                                                    "&:not(:has(.MuiSwitch-root, .MuiAutocomplete-root))": {
                                                        "&:hover": {
                                                            backgroundColor: alpha(
                                                                theme.palette.greySecondary[50],
                                                                0.5,
                                                            ),
                                                        },
                                                    },
                                                }}
                                            >
                                                {getComponentByType(attribution)}
                                            </ListItem>
                                        ))}
                                </List>
                            </Grid>
                        )}
                    </Stack>
                    {index < data.length - 1 && (
                        <Divider flexItem orientation={"horizontal"} sx={{ mt: 2.5, mb: 2.5 }} />
                    )}
                </div>
            ))}
            {!userProfilePending && (
                <UserDetailPopper
                    id={openUserProfile ? "user-profile-popover" : undefined}
                    handleUserProfileClose={handleUserProfileClose}
                    userProfileAnchorEl={userProfileAnchorEl}
                    openUserProfile={openUserProfile}
                    userProfileData={userProfileData}
                />
            )}
        </Box>
    );
};

export default PanelOverview;
